import { auth } from 'config/firebase';
import * as Sentry from '@sentry/react';

/**
 * User Service - Handles all user management operations
 */

// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  STAFF: 'staff',
  USER: 'user',
};

// User status
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
};

// API base URL
const API_BASE = '/.netlify/functions';

// Helper function to get auth token
const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
};

// Helper function to make API calls
const apiCall = async (endpoint, options = {}) => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    ...options,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  if (data.body && typeof data.body === 'string') {
    try {
      return JSON.parse(data.body);
    } catch {
      return data.body;
    }
  } else if (data.data !== undefined) {
    return data.data;
  }

  return data;
};

/**
 * Fetch all users from API
 */
export const fetchUsers = async () => {
  try {
    return await apiCall('/user-management?action=fetchUsers');
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'fetchUsers',
      },
    });
    throw new Error('Failed to fetch users', { cause: error });
  }
};

/**
 * Create a new user
 */
export const createUser = async (userData) => {
  try {
    return await apiCall('/user-management', {
      method: 'POST',
      body: JSON.stringify({
        action: 'createUser',
        userData,
      }),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'createUser',
      },
      extra: { userData },
    });
    throw new Error(error.message || 'Failed to create user');
  }
};

/**
 * Update user data
 */
export const updateUser = async (userId, updates) => {
  try {
    return await apiCall('/user-management', {
      method: 'PUT',
      body: JSON.stringify({
        action: 'updateUser',
        id: userId, // Changed from userId to id to match backend expectation
        ...updates,
      }),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'updateUser',
      },
      extra: { userId, updates },
    });
    // Keep the server's reason: the modal shows it, and "the role change didn't
    // apply, try again" is what the admin needs to act on (#480).
    throw new Error(error.message || 'Failed to update user');
  }
};

/**
 * Delete a user
 */
export const deleteUserAccount = async (userId) => {
  try {
    return await apiCall('/user-management', {
      method: 'DELETE',
      body: JSON.stringify({
        action: 'deleteUser',
        userId,
      }),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'deleteUserAccount',
      },
      extra: { userId },
    });
    throw new Error('Failed to delete user');
  }
};

/**
 * Get user by ID
 */
export const getUserById = async (userId) => {
  try {
    return await apiCall(
      `/user-management?action=getUserById&userId=${userId}`,
    );
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'getUserById',
      },
      extra: { userId },
    });
    throw new Error('Failed to fetch user', { cause: error });
  }
};

/**
 * Search users by email or display name
 */
export const searchUsers = async (searchTerm) => {
  try {
    const encodedTerm = encodeURIComponent(searchTerm || '');
    return await apiCall(
      `/user-management?action=searchUsers&searchTerm=${encodedTerm}`,
    );
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'searchUsers',
      },
      extra: { searchTerm },
    });
    throw new Error('Failed to search users');
  }
};

/**
 * Get users by role
 */
export const getUsersByRole = async (role) => {
  try {
    return await apiCall(`/user-management?action=getUsersByRole&role=${role}`);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'getUsersByRole',
      },
      extra: { role },
    });
    throw new Error('Failed to fetch users by role', { cause: error });
  }
};

/**
 * Update user last login time
 */
export const updateLastLogin = async (userId) => {
  try {
    await updateUser(userId, {
      lastLogin: new Date().toISOString(),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'userService',
        action: 'updateLastLogin',
      },
      extra: { userId },
    });
    // Don't throw error for this non-critical operation
  }
};
